/*
 * InputHijack.tsx — 超常演出「入力乗っ取り」（spec §3.3 / 没入 P4-1・2026-06-25）
 *
 * 体験: LINE の自由入力欄に文字を打っていると、ある瞬間ユーザーの入力が
 *       勝手に別のテキスト（hijackText）へ書き換わる。付喪神＝端末側の「現象」であり
 *       台詞ではない（世界観 §7.4：システム付喪神は無言。文言は demo 側が決める）。
 *
 * 設計上の肝:
 *  - 他の超常演出（message 系）は吹き出しを wrap する。input_hijack は唯一 freeInput に作用する
 *    （spec の category: 'input'）。そのため「ロジック本体」は本ファイルの useInputHijack フックに置き、
 *    LineDialogView の freeInput ハンドラから呼んで input へ配線する。レジストリには登録するが、
 *    レジストリ component は吹き出しを素通しする no-op（graceful degradation）にする。
 *  - IME 安全（最重要）: 日本語入力の変換中（composition 中）は文字数を数えない／発火しない。
 *    compositionstart/compositionend を監視し、確定済みテキスト長で triggerAfterChars を判定する。
 *    onChange の生 value で誤発火させない（未確定の読みで数えると誤爆するため）。
 *  - prefers-reduced-motion: shake は弱化（CSS 側で animation を抑制。本フックは class 付与のみ）。
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CompositionEvent,
  type JSX,
} from 'react';
import type { EffectProps } from './types';

/* ── パラメータ（spec §3.3 の既定値どおり） ──
 * demo 側が supernaturalEffect.params で上書きする想定。
 * defaults は registry の defaultParams と本フックの両方で同値を保つ。
 */
export interface InputHijackParams {
  /** 書き換え先テキスト（必須・空なら arm しない） */
  hijackText: string;
  /** 確定済み N 文字で発動（既定 5） */
  triggerAfterChars: number;
  /** 書き換えの 1 文字あたり ms（既定 50） */
  typeSpeed: number;
  /** 書き換え後にユーザーが再入力で上書き可能か（既定 true） */
  allowOverride: boolean;
  /** 書き換え時に入力欄を揺らすか（既定 true） */
  shakeOnHijack: boolean;
}

export const INPUT_HIJACK_DEFAULT_PARAMS: InputHijackParams = {
  hijackText: '',
  triggerAfterChars: 5,
  typeSpeed: 50,
  allowOverride: true,
  shakeOnHijack: true,
};

/** registry 由来の生 params（unknown）を型付き params に正規化する。 */
export function normalizeInputHijackParams(raw: Record<string, unknown> | undefined): InputHijackParams {
  const p = raw ?? {};
  const hijackText = typeof p.hijackText === 'string' ? p.hijackText : INPUT_HIJACK_DEFAULT_PARAMS.hijackText;
  const triggerAfterChars =
    typeof p.triggerAfterChars === 'number' && p.triggerAfterChars > 0
      ? Math.floor(p.triggerAfterChars)
      : INPUT_HIJACK_DEFAULT_PARAMS.triggerAfterChars;
  const typeSpeed =
    typeof p.typeSpeed === 'number' && p.typeSpeed >= 0 ? p.typeSpeed : INPUT_HIJACK_DEFAULT_PARAMS.typeSpeed;
  const allowOverride = typeof p.allowOverride === 'boolean' ? p.allowOverride : INPUT_HIJACK_DEFAULT_PARAMS.allowOverride;
  const shakeOnHijack = typeof p.shakeOnHijack === 'boolean' ? p.shakeOnHijack : INPUT_HIJACK_DEFAULT_PARAMS.shakeOnHijack;
  return { hijackText, triggerAfterChars, typeSpeed, allowOverride, shakeOnHijack };
}

export interface UseInputHijackArgs {
  /** input_hijack が arm 状態か（該当 supernaturalEffect を持つメッセージが表示済みか）。false なら完全に素通し。 */
  armed: boolean;
  /** 効果パラメータ（armed=false のときは無視される）。 */
  params: InputHijackParams;
  /**
   * 入力値を更新する（書き換え・ユーザー入力の両方をここへ集約）。
   * value 自体は LineDialogView が単一ソースとして保持するため、本フックは setter のみ受け取る。
   */
  setValue: (next: string) => void;
}

export interface UseInputHijackResult {
  /** input に spread する props（onChange/onCompositionStart/onCompositionEnd）。 */
  inputProps: {
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onCompositionStart: () => void;
    onCompositionEnd: (e: CompositionEvent<HTMLInputElement>) => void;
  };
  /** 書き換えアニメ中か（typeSpeed で 1 文字ずつ流し込み中）。 */
  isHijacking: boolean;
  /** 揺れ class を当てるべきか（shakeOnHijack && 発動直後）。 */
  shouldShake: boolean;
  /** allowOverride=false で固定済みか（再入力を弾く）。 */
  isLocked: boolean;
}

/**
 * input_hijack の本体ロジック。LineDialogView の freeInput から呼ぶ。
 *
 * フロー:
 *  1. arm: armed=true（該当メッセージ表示済み）かつ hijackText 非空のとき作動可能。
 *  2. IME 安全カウント: composing 中は数えない。確定テキスト長 >= triggerAfterChars で 1 回だけ発火。
 *  3. typeSpeed 書換: hijackText を 1 文字ずつ typeSpeed ms で value へ流し込む（書換中はユーザー入力を無視）。
 *  4. override: 書換完了後、allowOverride=true なら再編集可。false なら value を hijackText に固定（入力を弾く）。
 */
export function useInputHijack({ armed, params, setValue }: UseInputHijackArgs): UseInputHijackResult {
  const [isComposing, setIsComposing] = useState(false);
  const [isHijacking, setIsHijacking] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);
  // 一度発動したら再発動しない（同じ step 内で arm は 1 回限り）。
  const firedRef = useRef(false);
  const typeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shakeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 最新の setValue を ref 化（書換アニメのクロージャが古い setter を掴まないように）。
  const setValueRef = useRef(setValue);
  setValueRef.current = setValue;

  const enabled = armed && params.hijackText.length > 0;
  const isLocked = firedRef.current && !params.allowOverride;

  // 確定済み文字数（書記素ではなく code unit 長）で発火判定する純関数。
  const tryFire = useCallback(
    (confirmedText: string) => {
      if (!enabled || firedRef.current || isComposing) return;
      // 確定テキスト長（前後空白は数える＝ユーザーが打った確定文字数で素直に判定）。
      if (confirmedText.length < params.triggerAfterChars) return;

      firedRef.current = true;
      setIsHijacking(true);
      if (params.shakeOnHijack) {
        setShouldShake(true);
        shakeTimerRef.current = setTimeout(() => setShouldShake(false), 520);
      }

      // hijackText を 1 文字ずつ typeSpeed ms で流し込む。
      const target = params.hijackText;
      const speed = Math.max(0, params.typeSpeed);
      // まず空にしてからタイプし直す（「打った文字が消えて別物が浮かぶ」感）。
      setValueRef.current('');
      let idx = 0;
      const step = () => {
        idx += 1;
        setValueRef.current(target.slice(0, idx));
        if (idx >= target.length) {
          setIsHijacking(false);
          typeTimerRef.current = null;
          return;
        }
        typeTimerRef.current = setTimeout(step, speed);
      };
      // 1 文字目をわずかな間（speed）の後に出す。speed=0 でも setTimeout(…,0) で順序を保つ。
      typeTimerRef.current = setTimeout(step, speed);
    },
    [enabled, isComposing, params.triggerAfterChars, params.hijackText, params.typeSpeed, params.shakeOnHijack]
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      // 書換中はユーザー入力を完全に無視（プログラムによる流し込みのみが value を進める）。
      if (isHijacking) return;
      // ロック中（allowOverride:false で発動済み）は再入力を弾き、hijackText に固定し続ける。
      if (isLocked) {
        setValue(params.hijackText);
        return;
      }
      // 通常: まず value を反映。
      setValue(next);
      // IME 変換中は数えない（確定してから compositionend 側で判定する）。
      if (isComposing) return;
      tryFire(next);
    },
    [isHijacking, isLocked, isComposing, params.hijackText, setValue, tryFire]
  );

  const onCompositionStart = useCallback(() => {
    setIsComposing(true);
  }, []);

  const onCompositionEnd = useCallback(
    (e: CompositionEvent<HTMLInputElement>) => {
      setIsComposing(false);
      if (isHijacking) return;
      if (isLocked) {
        setValue(params.hijackText);
        return;
      }
      // 確定値を反映してから、確定テキスト長で発火判定する（IME 安全の要）。
      // CompositionEvent.target は EventTarget なので、値は currentTarget（input 要素）から読む。
      const confirmed = e.currentTarget.value;
      setValue(confirmed);
      tryFire(confirmed);
    },
    [isHijacking, isLocked, params.hijackText, setValue, tryFire]
  );

  // arm が解けたら（別 step へ遷移＝LineDialogView 側でフックが作り直される想定だが保険として）状態を戻す。
  useEffect(() => {
    if (!enabled) {
      firedRef.current = false;
      setIsHijacking(false);
      setShouldShake(false);
    }
  }, [enabled]);

  // アンマウント時にタイマを掃除（DOM への影響を残さない＝spec §6 クリーンアップ）。
  useEffect(() => {
    return () => {
      if (typeTimerRef.current) clearTimeout(typeTimerRef.current);
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    };
  }, []);

  return {
    inputProps: { onChange, onCompositionStart, onCompositionEnd },
    isHijacking,
    shouldShake,
    isLocked,
  };
}

/*
 * レジストリ用コンポーネント。
 * input_hijack は吹き出しを装飾しない（作用先は freeInput）。
 * wrapWithEffect から呼ばれた場合は子（元の吹き出し）をそのまま返す＝視覚的 no-op。
 * これにより「該当メッセージは普通に表示される」graceful degradation を保ちつつ、
 * 実際の乗っ取りは LineDialogView が useInputHijack 経由で freeInput に適用する。
 */
export function InputHijack({ children }: EffectProps): JSX.Element {
  return <>{children}</>;
}
